import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import { TableCell, TableRow } from "@/components/ui/table";
import { type BusinessExpense } from "@/hooks/useBusinessExpenses";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import { Download, Edit, Eye, Trash2 } from "lucide-react";
import { formatFullNumericDateUTC } from "../../../../../../utils/shared/dateUtils";

interface BusinessExpenseRowProps {
  expense: BusinessExpense | any; // Accept any to handle backend RPC response
  index: number;
  onEdit: (expense: BusinessExpense) => void;
  onDelete: (expense: BusinessExpense) => void;
  onViewReceipt: (receiptUrl: string) => void;
  canEdit: boolean;
  canDelete: boolean;
  canDownload: boolean;
  isSelected: boolean;
  onSelect: (expenseId: string, index: number, shiftKey: boolean) => void;
}

const BusinessExpenseRow = ({
  expense,
  index,
  onEdit,
  onDelete,
  onViewReceipt,
  canEdit,
  canDelete,
  canDownload,
  isSelected,
  onSelect,
}: BusinessExpenseRowProps) => (
  <TableRow>
    <TableCell onClick={(e) => onSelect(expense.id, index, e.shiftKey)}>
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => {}}
        aria-label={`Seleccionar egreso ${expense.id}`}
      />
    </TableCell>

    <TableCell>
      {expense.organization_name || "-"}
    </TableCell>

    <TableCell>
      {expense.property_short_name || expense.properties?.short_name || "-"}
    </TableCell>

    <TableCell>{formatFullNumericDateUTC(expense.payment_date)}</TableCell>

    <TableCell>
      {expense.expense_type_name || expense.business_expenses_types?.name || expense.expenses_type || "-"}
    </TableCell>

    <TableCell>
      {expense.expense_subtype_name || expense.business_expenses_subtypes?.name || "-"}
    </TableCell>

    <TableCell>{expense.payment_type || "-"}</TableCell>

    <TableCell>{expense.expense_description || "-"}</TableCell>

    <TableCell>{formatCurrency(expense.total_payment)}</TableCell>

    <TableCell>
      {expense.receipt_url ? (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewReceipt(expense.receipt_url!)}
          >
            <Eye className="h-4 w-4" />
          </Button>

          {canDownload && (
            <a
              href={expense.receipt_url.replace(
                "/upload/",
                `/upload/fl_attachment/`,
              )}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4" />
              </Button>
            </a>
          )}
        </div>
      ) : (
        <span className="text-muted-foreground">-</span>
      )}
    </TableCell>

    {(canEdit || canDelete) && (
      <TableCell>
        <div className="flex items-center space-x-2">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(expense)}>
              <Edit className="h-4 w-4" />
            </Button>
          )}

          {canDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(expense)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    )}
  </TableRow>
);

export default BusinessExpenseRow;
